import { DISCOUNT_METHOD, useDiscountContext } from 'lib/discount';
import { useCart } from '../CartContext';
import { POINT_NAME, useUserLoyaltyPoints } from 'lib/loyaltyProgram';
import { ProductLink } from 'lib/sellingProduct';
import { Price } from 'lib/core';
import {
  Radio,
  RadioGroup,
  Spinner,
  TextBalancer,
  Tooltip,
} from 'components/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddonOptionValues } from 'lib/productAddon';
import {
  getAppliedDiscountMethod,
  hasCouponApplied,
  hasLoyaltyPointsApplied,
} from '../utils';
import notification from 'lib/notification';
import {
  AppliedCoupons,
  ApplyCouponCodeForm,
  useApplyCouponCodeFormContext,
} from 'lib/coupons';
import cn from 'classnames';
import { FUEL_SURCHARGE } from 'lib/deliveryFee/constants';
import { BulletList } from 'react-content-loader';

type DiscountMethod = typeof DISCOUNT_METHOD[keyof typeof DISCOUNT_METHOD];

const getDiscountErrorMessage = (
  err: Record<string, any>,
): { message: string; title: string } => {
  if (
    err?.errors?.error_code === 'yb_reps.total_redeemable.not_enough'
  ) {
    return {
      message:
        '😂 You need at least 1 YB Rep to get a discount on your order! ♥️',
      title: "Oops! You don't have enough YB Reps!",
    };
  }

  return {
    message:
      err?.errors?.message ??
      'Hi! 👋 Please log in to use YB Reps for a discount! ♥️',
    title: 'Error changing discount method',
  };
};

interface CheckoutSummaryProps {
  showCartLines?: boolean;
  isDoorstepDelivery?: boolean;
  needUtensils?: boolean;
  donationOptions?: React.ReactNode | null;
  donationAmount?: number;
}

export const CheckoutSummary = ({
  showCartLines,
  isDoorstepDelivery = true,
  needUtensils,
  donationOptions,
  donationAmount = 0,
}: CheckoutSummaryProps) => {
  const [
    disabledDiscountMethod,
    setDisabledDiscountMethod,
  ] = useState(false);

  const {
    loading: isCartLoading,
    cart,
    applyCouponToCart,
    removeCouponFromCart,
    applyLoyaltyPointsToCart,
    removeLoyaltyPointsFromCart,
  } = useCart();

  const {
    method: discountMethod,
    setMethod: setDiscountMethod,
  } = useDiscountContext();

  const {
    values: couponCodeValues,
    errors: couponCodeErrors,
  } = useApplyCouponCodeFormContext();

  const {
    redeemableLoyaltyPoints,
    redeemableInDollarLoyaltyPoints,
  } = useUserLoyaltyPoints();

  const {
    items: cartLines,
    discounts,
    sub_total: subTotal,
    sub_total_after_discount: subTotalAfterDiscount,
    delivery_fee: deliveryFee,
    delivery_fee_after_discount: deliveryFeeAfterDiscount,
    collection_point_fee: collectionPointFee,
    tax,
    utensil,
  } = cart ?? {};

  const isEmptyCart = useMemo(
    () => !cartLines || cartLines.length < 1,
    [cartLines],
  );

  const finalDeliveryFee = useCallback(() => {
    if (isEmptyCart) return 0;

    return isDoorstepDelivery ? deliveryFee : collectionPointFee;
  }, [
    collectionPointFee,
    deliveryFee,
    isDoorstepDelivery,
    isEmptyCart,
  ]);

  const couponDiscount = useMemo(
    () => discounts?.sub_total_discount,
    [discounts],
  );

  const deliveryDiscount = useMemo(
    () => discounts?.delivery_discount,
    [discounts],
  );

  const isFreeDelivery = useMemo(
    () =>
      (isDoorstepDelivery && deliveryFee === 0) ||
      (isDoorstepDelivery &&
        deliveryDiscount > 0 &&
        deliveryDiscount === finalDeliveryFee()),
    [
      deliveryDiscount,
      deliveryFee,
      finalDeliveryFee,
      isDoorstepDelivery,
    ],
  );

  const cartGrandTotal = useMemo((): number => {
    if (isEmptyCart) return 0;

    const calcGrandTotal = ({
      subTotal,
      deliveryFee,
      needUtensils,
      utensilPrice,
      donationAmount,
    }: {
      subTotal: number;
      deliveryFee: number;
      needUtensils?: boolean;
      utensilPrice: number;
      donationAmount?: number;
    }) => {
      return (
        subTotal +
        deliveryFee +
        (needUtensils ? utensilPrice : 0) +
        (donationAmount ?? 0)
      );
    };

    if (discountMethod === DISCOUNT_METHOD.NONE) {
      return calcGrandTotal({
        subTotal,
        deliveryFee: isDoorstepDelivery
          ? deliveryFee
          : collectionPointFee,
        needUtensils,
        utensilPrice: utensil.price,
        donationAmount,
      });
    }

    return calcGrandTotal({
      subTotal: subTotalAfterDiscount,
      deliveryFee: isDoorstepDelivery
        ? deliveryFeeAfterDiscount
        : collectionPointFee,
      needUtensils,
      utensilPrice: utensil.price,
      donationAmount,
    });
  }, [
    isEmptyCart,
    discountMethod,
    subTotalAfterDiscount,
    isDoorstepDelivery,
    deliveryFeeAfterDiscount,
    collectionPointFee,
    subTotal,
    deliveryFee,
    needUtensils,
    utensil.price,
    donationAmount,
  ]);

  const cartTotalTax = useMemo((): number => {
    let totalTax = isEmptyCart ? 0 : tax;
    if (needUtensils) totalTax += utensil.tax;
    return totalTax;
  }, [isEmptyCart, needUtensils, tax, utensil]);

  const removeAppliedDiscountMethod = useCallback(async () => {
    if (hasCouponApplied(cart)) {
      await Promise.all(
        discounts.coupons.map((coupon) =>
          removeCouponFromCart(coupon.coupon_code),
        ),
      );
    }

    if (hasLoyaltyPointsApplied(cart)) {
      await removeLoyaltyPointsFromCart();
    }
  }, [
    cart,
    discounts,
    removeCouponFromCart,
    removeLoyaltyPointsFromCart,
  ]);

  const handleDiscountMethodChange = useCallback(
    async (method: DiscountMethod) => {
      try {
        setDisabledDiscountMethod(true);
        setDiscountMethod(method);

        await removeAppliedDiscountMethod();
        try {
          switch (method) {
            case DISCOUNT_METHOD.LOYALTY_POINTS:
              return await applyLoyaltyPointsToCart();

            default:
              return;
          }
        } catch (error) {
          throw error;
        }
      } catch (error) {
        const { message, title } = getDiscountErrorMessage(error);
        notification.error(message, title);
        setDiscountMethod(DISCOUNT_METHOD.NONE);
        setDisabledDiscountMethod(false);
      } finally {
        setDisabledDiscountMethod(false);
      }
    },
    [
      applyLoyaltyPointsToCart,
      removeAppliedDiscountMethod,
      setDiscountMethod,
    ],
  );

  useEffect(() => {
    const appliedDiscountMethod = getAppliedDiscountMethod(cart);
    if (!discountMethod) setDiscountMethod(appliedDiscountMethod);
  }, [cart, discountMethod, setDiscountMethod]);

  if (isCartLoading || !cart) return <BulletList />;

  return (
    <>
      {showCartLines && (
        <>
          <div className="tw-flex tw-justify-between tw-pb-[2rem] tw-text-[#333] tw-font-semibold tw-text-[1.6rem] tw-leading-normal tw-gap-32 md:tw-gap-16">
            <span>Product</span>
            <span>Total</span>
          </div>
          <div className="tw-pt-[12px] tw-space-y-[12px] tw-leading-normal">
            {cartLines.map((cartLine) => (
              <div
                key={cartLine.id}
                className="tw-flex tw-justify-between tw-items-start tw-gap-32 md:tw-gap-16 tw-pb-[12px] tw-border-b"
              >
                <ProductLink
                  product={cartLine.product}
                  className="tw-mb-0 tw-text-[14px] tw-text-[#333]"
                >
                  <span className="tw-leading-normal">
                    {cartLine.product.name}
                    &nbsp;
                    <span className="tw-font-semibold tw-whitespace-nowrap tw-bg-gray-backdrop-dark tw-py-1 tw-px-2.5 tw-leading-none tw-text-[#777] tw-text-[11px] md:tw-text-[12px] tw-rounded-2xl tw-inline-block">
                      {' '}
                      x{cartLine.quantity}
                    </span>
                  </span>
                  {cartLine.addon_option_values.length > 0 && (
                    <div className="tw-text-[12px] tw-text-[#777] tw-font-normal tw-mt-1.5 tw-leading-normal">
                      <AddonOptionValues
                        data={cartLine.addon_option_values}
                      />
                    </div>
                  )}
                </ProductLink>
                <span className="tw-text-[#666] tw-font-semibold tw-shrink-0">
                  <Price value={cartLine.total} />
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="tw-flex tw-justify-between tw-py-[12px] tw-text-[#333] tw-font-semibold tw-text-[1.6rem] tw-leading-normal tw-gap-32 md:tw-gap-16 tw-border-b">
        <span>Subtotal:</span>
        <span>
          <Price value={subTotal} />
        </span>
      </div>
      <div className="tw-flex tw-justify-between tw-py-[12px] tw-text-[#333] tw-font-semibold tw-text-[1.6rem] tw-leading-normal tw-gap-32 md:tw-gap-16">
        <span className="tw-flex tw-items-center tw-gap-4">
          Discounts:{' '}
          {disabledDiscountMethod && (
            <Spinner size="sm" variant="light" />
          )}
        </span>
      </div>
      <RadioGroup
        name="discount"
        value={discountMethod}
        onChange={handleDiscountMethodChange}
        disabled={disabledDiscountMethod}
        className="tw-mb-[12px]"
      >
        <div className="tw-space-y-4 tw-text-[#333] tw-min-w-0">
          <Radio
            value={DISCOUNT_METHOD.NONE}
            label="No&nbsp;redemption/coupon"
            className="tw-leading-normal"
          />
          <div className="tw-flex tw-justify-between tw-gap-32 md:tw-gap-16">
            <Radio
              value={DISCOUNT_METHOD.LOYALTY_POINTS}
              // @ts-ignore
              label={
                <>
                  Redeem {redeemableLoyaltyPoints} {POINT_NAME}
                  <br />
                  <TextBalancer as="small">
                    Max redemption per order: $10
                  </TextBalancer>
                </>
              }
              className="tw-leading-normal"
            />
            {discountMethod === DISCOUNT_METHOD.LOYALTY_POINTS && (
              <span className="tw-font-semibold">
                <Price value={-1 * redeemableInDollarLoyaltyPoints} />
              </span>
            )}
          </div>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-justify-between tw-gap-32 md:tw-gap-16">
              <Radio
                value={DISCOUNT_METHOD.COUPON}
                label="Coupon Code"
                className="tw-leading-normal"
              />
              {discountMethod === DISCOUNT_METHOD.COUPON && (
                <span className="tw-font-semibold">
                  <Price value={-1 * couponDiscount} />
                </span>
              )}
            </div>
            {discountMethod === DISCOUNT_METHOD.COUPON && (
              <>
                {discounts?.coupons.length === 0 ? (
                  <ApplyCouponCodeForm
                    initialValues={couponCodeValues}
                    initialErrors={couponCodeErrors}
                    onSubmit={({ coupon_code }) =>
                      applyCouponToCart(coupon_code)
                    }
                  />
                ) : (
                  <AppliedCoupons
                    discounts={discounts}
                    onRemoveCoupon={(couponCode) =>
                      removeCouponFromCart(couponCode)
                    }
                  />
                )}
              </>
            )}
          </div>
        </div>
      </RadioGroup>
      <div
        className={cn(
          'tw-flex tw-justify-between tw-font-semibold tw-text-[1.4rem] tw-leading-normal tw-gap-32 md:tw-gap-16 tw-border-b tw-pb-[12px]',
        )}
      >
        <span>Delivery:</span>
        <div className="tw-flex tw-justify-end tw-gap-4">
          {isFreeDelivery && <span>FREE</span>}
          {isFreeDelivery ? (
            <span className="tw-line-through">
              <Price value={finalDeliveryFee()} />
            </span>
          ) : (
            <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-end tw-leading-normal tw-flex-1">
              <Price value={finalDeliveryFee()} />
              {finalDeliveryFee() > 0 && (
                <p className="tw-text-[#333] tw-mb-0 tw-text-right">
                  <small>
                    Includes ${FUEL_SURCHARGE} Fuel Surcharge
                  </small>
                  <Tooltip
                    id={`fuel-surcharge-${FUEL_SURCHARGE}`}
                    key={`fuel-surcharge-${FUEL_SURCHARGE}`}
                    content="A $1 fuel surcharge applies to orders below $150 due to rising fuel costs. 100% goes to our delivery drivers. This will be lifted when costs stabilise."
                    placement="bottom"
                    contentClassName="tw-text-[11px] tw-leading-snug"
                  >
                    <i
                      className="fas fa-exclamation-circle"
                      style={{
                        verticalAlign: 'middle',
                        marginLeft: '4px',
                        marginTop: '2px',
                        cursor: 'pointer',
                        fontSize: '1.25rem',
                      }}
                    />
                  </Tooltip>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      {needUtensils && (
        <div className="tw-flex tw-justify-between tw-font-semibold tw-text-[1.4rem] tw-leading-normal tw-gap-32 md:tw-gap-16 tw-border-b tw-py-[12px]">
          <span>Utensil:</span>
          <Price value={utensil.price} />
        </div>
      )}
      {donationOptions}
      <div
        className={cn(
          'tw-flex tw-justify-between tw-text-[#333] tw-font-semibold tw-text-[1.6rem] tw-leading-normal tw-gap-32 md:tw-gap-16 tw-items-center',
          showCartLines ? 'tw-pt-[12px]' : 'tw-py-[12px]',
        )}
      >
        <span>Total:</span>
        <div className="tw-flex tw-items-end tw-flex-col tw-shrink-0">
          <Price value={cartGrandTotal} />
          <p className="tw-mb-0">
            <small>
              Includes <Price value={cartTotalTax} />
              &nbsp;GST
            </small>
          </p>
        </div>
      </div>
    </>
  );
};
